/** @file Primitive zod schemas shared by the document schema parts (ids, names, colors, curves, keys). */
import { z } from 'zod';
import { ids, type IdPrefix } from '../../ids';
import { names } from '../../names';

const finite = z.number();
/** Inherit modes in code order: the rig JSON stores the index (0 normal … 4 noScaleOrReflection). */
const INHERIT = ['normal', 'onlyTranslation', 'noRotationOrReflection', 'noScale', 'noScaleOrReflection'] as const;
/** Blend modes in code order: the rig JSON stores the index (0 normal … 3 screen). */
const BLEND = ['normal', 'additive', 'multiply', 'screen'] as const;
const hex8 = z.string().regex(/^#[0-9a-fA-F]{8}$/, 'expected #rrggbbaa');
const hex6 = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'expected #rrggbb');

/** A `Curve` on a key: linear, stepped, CSS-like ease, or graph-editor bezier handles. */
const curve = z.union([
  z.literal('linear'),
  z.literal('stepped'),
  z.object({ ease: z.tuple([finite, finite, finite, finite]) }).strict(),
  z.object({ bezier: z.tuple([finite, finite, finite, finite]) }).strict(),
]);

const localTransform = z
  .object({ x: finite, y: finite, rotation: finite, scaleX: finite, scaleY: finite, shearX: finite, shearY: finite })
  .strict();

export const prim = {
  /** An id of one entity kind only (`prim.idOf('bone')` rejects slot ids). */
  idOf(prefix: IdPrefix) {
    return z.string().regex(ids.re(prefix), `expected a ${prefix}_ id`);
  },
  name: z.string().regex(names.RE, 'invalid name'),
  animationName: z.string().regex(names.ANIMATION_RE, 'invalid animation name'),
  finite,
  frame: z.number().int().nonnegative(),
  unit: z.number().min(0).max(1),
  /** `#rrggbbaa` (setup colors). */
  hex8,
  /** `#rrggbb` (rgb / dark keys; alpha is its own channel). */
  hex6,
  /** Plain numeric arrays only: a typed array is not an Array and fails this schema. */
  numbers: z.array(finite),
  px: z.tuple([finite, finite]),
  curve,
  localTransform,
  inherit: z.enum(INHERIT),
  blend: z.enum(BLEND),
  /** Rig JSON code of an inherit mode = its index in `prim.inherit.options`. */
  inheritCode: z.number().int().min(0).max(INHERIT.length - 1),
  /** Rig JSON code of a blend mode = its index in `prim.blend.options`. */
  blendCode: z.number().int().min(0).max(BLEND.length - 1),
  dir: z.enum(names.DIRS),
  /** A numeric-channel keyframe `{ f, v, curve? }`. */
  key<V extends z.ZodType>(value: V) {
    return z.object({ f: z.number().int().nonnegative(), v: value, curve: curve.optional() }).strict();
  },
  /** A discrete-channel keyframe `{ f, v }`: attachment, inherit and boolean keys never carry a curve. */
  discreteKey<V extends z.ZodType>(value: V) {
    return z.object({ f: z.number().int().nonnegative(), v: value }).strict();
  },
};

export type Curve = z.infer<typeof curve>;
export type LocalTransform = z.infer<typeof localTransform>;
export type Inherit = z.infer<typeof prim.inherit>;
export type BlendName = z.infer<typeof prim.blend>;
export type Hex8 = z.infer<typeof hex8>;
export type Hex6 = z.infer<typeof hex6>;
